import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Edit2, Trash2, Eye, Users,BookOpen, Calendar, Shield, X, AlertTriangle, UserCheck, Building, Mail } from 'lucide-react';
import styles from '../CSS/RoleList.module.css';

const RoleList = ({ 
  roles, 
  availablePermissions, 
  onUpdate, 
  showFeedback, 
  onEditRole, 
  onDeleteRole, 
  canDeleteRole, 
  currentUser, 
  users,
  organizationalUnits = []
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [roleToView, setRoleToView] = useState(null);

  const getPermissionName = (permissionId) => {
    const permission = availablePermissions.find(p => p.id === permissionId);
    return permission ? permission.name : permissionId;
  };

  const getPermissionColor = (permissionId) => {
    const colors = {
      'gestion': '#9f7aea',
      'correos': '#ed8936',
      'clases': '#38a169',
      'reservas': '#3182ce',
      'incidencias': '#e53e3e',
      'admin': '#d69e2e'
    };
    return colors[permissionId] || '#6b7280';
  };

  const getOUInfo = (role) => {
    const hasEmailPermission = role.permissions?.includes('correos');
    
    if (!hasEmailPermission) {
      return null;
    }

    const emailPerms = role.emailPermissions || {};
    const allowedOUs = emailPerms.allowedOUs || [];
    
    const countAllOUs = (ous) => {
      let count = 0;
      ous.forEach(ou => {
        count++;
        if (ou.children && ou.children.length > 0) {
          count += countAllOUs(ou.children);
        }
      });
      return count;
    };
    
    const totalOUs = countAllOUs(organizationalUnits);
    const hasAll = allowedOUs.length === totalOUs && totalOUs > 0;
    
    return {
      hasEmailPermission: true,
      hasAll,
      allowedOUs,
      count: allowedOUs.length,
      totalAvailable: totalOUs
    };
  };

  const getOUNames = (ouPaths) => {
    const names = [];
    
    const findOUName = (ous, targetPath) => {
      for (const ou of ous) {
        if (ou.orgUnitPath === targetPath) {
          return ou.name;
        }
        if (ou.children && ou.children.length > 0) {
          const found = findOUName(ou.children, targetPath);
          if (found) return found;
        }
      }
      return null;
    };
    
    ouPaths.forEach(path => {
      const name = findOUName(organizationalUnits, path);
      if (name) {
        names.push({ path, name });
      }
    });
    
    return names;
  };

  const handleViewDetails = (role) => {
    setRoleToView(role);
    setShowDetailsModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleDeleteClick = (role) => {
    const deleteCheck = canDeleteRole(role.id);
    
    if (!deleteCheck.canDelete) {
      showFeedback("error", deleteCheck.reason);
      return;
    }
    
    setRoleToDelete(role);
    setShowDeleteModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    const success = await onDeleteRole(roleToDelete.id);
    if (success) {
      setShowDeleteModal(false);
      setRoleToDelete(null);
      document.body.style.overflow = 'unset';
    }
  };

  const handleEditClick = (role) => {
    onEditRole(role);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setRoleToView(null);
    document.body.style.overflow = 'unset';
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setRoleToDelete(null);
    document.body.style.overflow = 'unset';
  };

  const getRoleUsageInfo = (roleId) => {
    const usersWithRole = users?.filter(user => user.role === roleId) || [];
    return {
      count: usersWithRole.length,
      users: usersWithRole
    };
  };

  const getRoleType = (roleId) => {
    if (roleId === 'admin' || roleId === 'user') {
      return 'system';
    }
    return 'custom';
  };

  // Componente del Modal de Detalles
  const DetailsModal = () => {
    const deleteCheck = canDeleteRole(roleToView.id);
    const ouInfo = getOUInfo(roleToView);
    
    return (
      <div className={styles.modalOverlay} onClick={closeDetailsModal}>
        <div className={styles.detailsModal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.detailsModalHeader}>
            <div className={styles.modalHeaderInfo}>
              <h3 className={styles.modalTitle}>{roleToView.name}</h3>
              <div className={styles.roleMetadata}>
                {getRoleType(roleToView.id) === 'system' && (
                  <span className={styles.systemRoleBadge}>
                    <Shield size={12} />
                    Rol del sistema
                  </span>
                )}
                {roleToView.id === currentUser?.role && (
                  <span className={styles.currentUserRoleBadge}>
                    <UserCheck size={12} />
                    Tu rol actual
                  </span>
                )}
              </div>
            </div>
            <button
              className={styles.closeButton}
              onClick={closeDetailsModal}
              title="Cerrar"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className={styles.detailsModalContent}>
            {roleToView.description && (
              <div className={styles.descriptionSection}>
                <h4 className={styles.sectionTitle}>Descripción</h4>
                <p className={styles.modalDescription}>{roleToView.description}</p>
              </div>
            )}
            
            <div className={styles.permissionsSection}>
              <h4 className={styles.sectionTitle}>
                <Shield size={16} />
                Permisos ({roleToView.permissions?.length || 0})
              </h4>
              <div className={styles.modalPermissionsList}>
                {roleToView.permissions?.length > 0 ? (
                  roleToView.permissions.map(permissionId => (
                    <span 
                      key={permissionId} 
                      className={styles.modalPermissionTag}
                      style={{ 
                        backgroundColor: `${getPermissionColor(permissionId)}20`,
                        color: getPermissionColor(permissionId),
                        borderColor: `${getPermissionColor(permissionId)}40`
                      }}
                    >
                      {getPermissionName(permissionId)}
                    </span>
                  ))
                ) : (
                  <span className={styles.noPermissions}>Sin permisos asignados</span>
                )}
              </div>
            </div>

            {ouInfo && (
              <div className={styles.ouSectionMinimal}>
                <h4 className={styles.sectionTitle}>
                  <Building size={16} />
                  Unidades Organizativas
                </h4>
                
                {ouInfo.hasAll ? (
                  <div className={styles.ouInfoText}>
                    ✅ Acceso a todas las unidades ({ouInfo.totalAvailable})
                  </div>
                ) : ouInfo.count === 0 ? (
                  <div className={styles.ouInfoText}>
                    ⚠️ Sin acceso a unidades organizativas
                  </div>
                ) : (
                  <>
                    <div className={styles.ouTagsContainer}>
                      {getOUNames(ouInfo.allowedOUs).map(({ path, name }) => (
                        <span key={path} className={styles.ouTagMinimal} title={path}>
                          {name}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            
              {roleToView.permissions?.includes('clases') && roleToView.classPermissions && (
                <div className={styles.ouSectionMinimal}>
                  <h4 className={styles.sectionTitle}>
                    <BookOpen size={16} />
                    Clases
                  </h4>
                  
                  {(() => {
                    const classPerms = roleToView.classPermissions;
                    const allowedGroups = classPerms.allowedGroups || [];
                    const totalGroups = 3; // DBH, LH, Botiquin
                    const hasAll = classPerms.canManageAll || (allowedGroups.length === totalGroups && totalGroups > 0);
                    
                    // Mapeo de IDs a nombres legibles
                    const groupNames = {
                      'DBH': { name: 'DBH', icon: '📚' },
                      'LH': { name: 'LH', icon: '🎓' },
                      'Botiquin': { name: 'Botiquín', icon: '🏥' }
                    };
                    
                    if (hasAll) {
                      return (
                        <div className={styles.ouInfoText}>
                          ✅ Acceso a todos los grupos ({totalGroups})
                        </div>
                      );
                    } else if (allowedGroups.length === 0) {
                      return (
                        <div className={styles.ouInfoText}>
                          ⚠️ Sin acceso a grupos de clases
                        </div>
                      );
                    } else {
                      return (
                        <div className={styles.ouTagsContainer}>
                          {allowedGroups.map(groupId => {
                            const groupData = groupNames[groupId];
                            return (
                              <span 
                                key={groupId} 
                                className={styles.ouTagMinimal} 
                                title={`Grupo ${groupId}`}
                              >
                                {groupData?.icon} {groupData?.name || groupId}
                              </span>
                            );
                          })}
                        </div>
                      );
                    }
                  })()}
                </div>
              )}


            {!deleteCheck.canDelete && (
              <div className={styles.protectionSection}>
                <h4 className={styles.sectionTitle}>
                  <Shield size={16} />
                  Protecciones del Sistema
                </h4>
                <div className={styles.protectionWarning}>
                  <AlertTriangle size={16} />
                  <span>{deleteCheck.reason}</span>
                </div>
              </div>
            )}
            
            <div className={styles.dateSection}>
              <h4 className={styles.sectionTitle}>Información de fechas</h4>
              <div className={styles.dateInfo}>
                <span className={styles.modalCreatedDate}>
                  <Calendar size={14} />
                  Creado: {roleToView.createdAt ? new Date(roleToView.createdAt).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Fecha desconocida'}
                </span>
                {roleToView.updatedAt && (
                  <span className={styles.modalUpdatedDate}>
                    🔄 Actualizado: {new Date(roleToView.updatedAt).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className={styles.detailsModalFooter}>
            <button
              className={`${styles.modalButton} ${styles.editModalButton}`}
              onClick={() => {
                closeDetailsModal();
                onEditRole(roleToView);
              }}
            >
              <Edit2 size={16} />
              Editar Rol
            </button>
            <button
              className={`${styles.modalButton} ${styles.closeModalButton}`}
              onClick={closeDetailsModal}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Componente del Modal de Eliminación
  const DeleteModal = () => {
    const deleteCheck = canDeleteRole(roleToDelete?.id);
    
    return (
      <div className={styles.modalOverlay} onClick={closeDeleteModal}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>¿Confirmar eliminación?</h3>
          </div>
          <div className={styles.modalContent}>
            <p>
              Esta acción no se puede deshacer. Se eliminará permanentemente el rol
              <strong> "{roleToDelete?.name}"</strong>.
            </p>
            
            {deleteCheck.usersAffected > 0 && (
              <div className={styles.reassignmentBox}>
                <strong>🔄 Reasignación Automática:</strong>
                <p>
                  {deleteCheck.usersAffected} usuario{deleteCheck.usersAffected !== 1 ? 's' : ''} 
                  {deleteCheck.usersAffected === 1 ? ' será reasignado ' : ' serán reasignados '} 
                  automáticamente al rol "Usuario".
                </p>
              </div>
            )}
            
            <div className={styles.warningBox}>
              <strong>⚠️ Advertencia:</strong> Esta operación es irreversible y creará un registro de auditoría en el sistema.
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button
              className={`${styles.modalButton} ${styles.cancelButton}`}
              onClick={closeDeleteModal}
            >
              Cancelar
            </button>
            <button
              className={`${styles.modalButton} ${styles.confirmButton}`}
              onClick={handleDeleteConfirm}
            >
              <Trash2 size={16} />
              Eliminar Rol
              {deleteCheck.usersAffected > 0 && ` y Reasignar (${deleteCheck.usersAffected})`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {roles.length === 0 ? (
        <div className={styles.emptyState}>
          <Users size={48} className={styles.emptyIcon} />
          <h3 className={styles.emptyTitle}>No hay roles creados</h3>
          <p className={styles.emptyDescription}>
            Crea tu primer rol personalizado para gestionar permisos de usuarios
          </p>
        </div>
      ) : (
        <div className={styles.roleGrid}>
          {roles.map(role => {
            const deleteCheck = canDeleteRole(role.id);
            const roleUsage = getRoleUsageInfo(role.id);
            const isSystemRole = getRoleType(role.id) === 'system';
            const isCurrentUserRole = role.id === currentUser?.role;

            return (
              <div key={role.id} className={`${styles.roleCard} ${isSystemRole ? styles.systemRoleCard : ''} ${isCurrentUserRole ? styles.currentUserRoleCard : ''}`}>
                <div className={styles.roleHeader}>
                  <div className={styles.roleInfo}>
                    <div className={styles.roleNameSection}>
                      <h3 className={styles.roleName}>{role.name}</h3>
                      <div className={styles.roleBadges}>
                        {isSystemRole && (
                          <span className={styles.systemBadge}>
                            <Shield size={10} />
                            Sistema
                          </span>
                        )}
                        {isCurrentUserRole && (
                          <span className={styles.currentUserBadge}>
                            <UserCheck size={10} />
                            Tu rol
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {roleUsage.count > 0 && (
                      <div className={styles.roleUsageInfo}>
                        <Users size={12} />
                        <span>{roleUsage.count} usuario{roleUsage.count !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className={styles.cardActions}>
                    <button
                      className={`${styles.actionButton} ${styles.viewButton}`}
                      onClick={() => handleViewDetails(role)}
                      title="Ver detalles"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.editButton}`}
                      onClick={() => handleEditClick(role)}
                      title="Editar rol"
                    >
                      <Edit2 size={16} />
                    </button>
                    {deleteCheck.canDelete && (
                      <button
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                        onClick={() => handleDeleteClick(role)}
                        title="Eliminar rol"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showDetailsModal && roleToView && createPortal(
        <DetailsModal />,
        document.body
      )}

      {showDeleteModal && roleToDelete && createPortal(
        <DeleteModal />,
        document.body
      )}
    </div>
  );
};

export default RoleList;